'use client';

import {
    ArrowUpTrayIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    PencilIcon,
    PhotoIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';

const IMAGE_PATH_HINTS = /(?:image|media|photo|picture|thumbnail|logo|avatar)/i;
const IMAGE_VALUE_HINTS =
    /(?:src|url|file|path|image|media|photo|picture|logo|avatar)/i;
const ICON_PATH_HINTS = /(?:^|[.\]])(?:icon|iconclass)$/i;

function readImageSource(node) {
    if (node.tagName === 'IMG') {
        return String(node.getAttribute('src') || node.currentSrc || '').trim();
    }

    const backgroundImage =
        node.style?.backgroundImage ||
        node.ownerDocument?.defaultView?.getComputedStyle(node)
            ?.backgroundImage ||
        '';
    const match = String(backgroundImage).match(/url\(["']?(.*?)["']?\)/i);
    return match?.[1] ? String(match[1]).trim() : '';
}

function readIconSource(node) {
    return node.tagName === 'I'
        ? String(node.getAttribute('class') || '').trim()
        : '';
}

function isImagePath(path) {
    const leaf =
        String(path || '')
            .split('.')
            .pop() || '';
    return IMAGE_PATH_HINTS.test(path) || IMAGE_VALUE_HINTS.test(leaf);
}

function isIconPath(path) {
    return ICON_PATH_HINTS.test(
        String(path || '')
            .split('[')
            .pop() || ''
    );
}

function normalizeImagePath(value) {
    try {
        const parsed = new URL(
            String(value || '').trim(),
            'https://editor.local'
        );
        const optimizedUrl = parsed.searchParams.get('url');
        if (optimizedUrl && parsed.pathname.includes('/_next/image')) {
            return normalizeImagePath(optimizedUrl);
        }
        return decodeURIComponent(parsed.pathname)
            .replace(/\/+$/, '')
            .toLowerCase();
    } catch {
        return String(value || '')
            .trim()
            .replace(/[?#].*$/, '')
            .replace(/\/+$/, '')
            .toLowerCase();
    }
}

function findImageWarning(warnings, source) {
    if (!warnings) return {};
    const direct = warnings.get(source);
    if (direct) return direct;
    const normalized = normalizeImagePath(source);
    for (const [url, warning] of warnings.entries()) {
        if (normalizeImagePath(url) === normalized) return warning;
    }
    return {};
}

function findImageField(value, target, path = '') {
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value).trim() === target &&
            path &&
            (isImagePath(path) || isIconPath(path))
            ? { key: path, value: String(value), isIcon: isIconPath(path) }
            : null;
    }

    if (!value || typeof value !== 'object') return null;

    if (Array.isArray(value)) {
        for (let index = 0; index < value.length; index += 1) {
            const match = findImageField(
                value[index],
                target,
                `${path}[${index}]`
            );
            if (match) return match;
        }
        return null;
    }

    for (const [property, child] of Object.entries(value)) {
        const childPath = path ? `${path}.${property}` : property;
        const match = findImageField(child, target, childPath);
        if (match) return match;
    }

    return null;
}

function findIconField(value, target, path = '') {
    if (typeof value === 'string' || typeof value === 'number') {
        const fieldValue = String(value).trim();
        const targetTokens = fieldValue.split(/\s+/).filter(Boolean);
        const nodeTokens = String(target || '')
            .split(/\s+/)
            .filter(Boolean);
        return targetTokens.length > 0 &&
            targetTokens.every((token) => nodeTokens.includes(token)) &&
            path &&
            isIconPath(path)
            ? { key: path, value: fieldValue, isIcon: true }
            : null;
    }

    if (!value || typeof value !== 'object') return null;
    if (Array.isArray(value)) {
        for (let index = 0; index < value.length; index += 1) {
            const match = findIconField(
                value[index],
                target,
                `${path}[${index}]`
            );
            if (match) return match;
        }
        return null;
    }

    for (const [property, child] of Object.entries(value)) {
        const childPath = path ? `${path}.${property}` : property;
        const match = findIconField(child, target, childPath);
        if (match) return match;
    }
    return null;
}

function sameTargets(previous, next) {
    return (
        previous.length === next.length &&
        previous.every(
            (item, index) =>
                item.node === next[index].node &&
                item.key === next[index].key &&
                item.value === next[index].value &&
                item.isIcon === next[index].isIcon &&
                item.missingAltText === next[index].missingAltText &&
                item.heavy === next[index].heavy
        )
    );
}

export default function InlineImageControls({
    rootRef,
    componentProps,
    imageWarnings,
    onReplace,
    onUpload,
    onSeoWarning,
}) {
    const [targets, setTargets] = useState([]);
    const [hoveredNode, setHoveredNode] = useState(null);
    const [, setLayoutTick] = useState(0);
    const hoverTimerRef = useRef(null);
    const uploadInputRefs = useRef(new Map());
    const [uploadingKey, setUploadingKey] = useState(null);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;

        let frameId = 0;
        const syncTargets = () => {
            window.cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(() => {
                const nextTargets = Array.from(
                    root.querySelectorAll('img, i, [style*="background-image"]')
                )
                    .map((node) => {
                        const source = readImageSource(node);
                        const iconSource = readIconSource(node);
                        const field = source
                            ? findImageField(componentProps, source)
                            : iconSource
                            ? findIconField(componentProps, iconSource)
                            : null;
                        const warnings = field
                            ? findImageWarning(imageWarnings, source)
                            : {};
                        return field
                            ? {
                                  node,
                                  key: field.key,
                                  value: field.value,
                                  isIcon: Boolean(field.isIcon),
                                  missingAltText: Boolean(
                                      warnings.missingAltText
                                  ),
                                  heavy: Boolean(warnings.heavy),
                              }
                            : null;
                    })
                    .filter(Boolean);

                // A before/after slider is one visual media block even though
                // it renders two overlapping image elements. Keep its warning
                // indicators stable across both sides; replace and upload
                // actions still use the image currently under the pointer.
                const compareWarnings = new Map();
                nextTargets.forEach((target) => {
                    const compareRoot =
                        target.node.closest('[data-rcs="root"]');
                    if (!compareRoot) return;
                    const combined = compareWarnings.get(compareRoot) || {
                        missingAltText: false,
                        heavy: false,
                    };
                    combined.missingAltText ||= target.missingAltText;
                    combined.heavy ||= target.heavy;
                    compareWarnings.set(compareRoot, combined);
                });
                nextTargets.forEach((target) => {
                    const compareRoot =
                        target.node.closest('[data-rcs="root"]');
                    const combined = compareRoot
                        ? compareWarnings.get(compareRoot)
                        : null;
                    if (!combined) return;
                    target.missingAltText = combined.missingAltText;
                    target.heavy = combined.heavy;
                });

                setTargets((previous) =>
                    sameTargets(previous, nextTargets) ? previous : nextTargets
                );
                setLayoutTick((value) => value + 1);
            });
        };

        syncTargets();
        const observer = new MutationObserver(syncTargets);
        observer.observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['src'],
        });
        window.addEventListener('resize', syncTargets);
        root.addEventListener('scroll', syncTargets, true);

        return () => {
            window.cancelAnimationFrame(frameId);
            observer.disconnect();
            window.removeEventListener('resize', syncTargets);
            root.removeEventListener('scroll', syncTargets, true);
        };
    }, [componentProps, imageWarnings, rootRef]);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;

        // Keep the toolbar visible while the pointer travels from the image
        // to the pill. The toolbar is an overlay sibling, so listening for a
        // mouseleave on the image itself creates a gap where it can disappear
        // before the button receives the click.
        const clearHover = () => setHoveredNode(null);
        const compareGroups = new Map();
        targets.forEach((target) => {
            const compareRoot = target.node.closest('[data-rcs="root"]');
            if (!compareRoot) return;
            const group = compareGroups.get(compareRoot) || {};
            if (target.node.closest('[data-rcs="clip-item"]')) {
                group.after = target;
            } else {
                group.before = target;
            }
            compareGroups.set(compareRoot, group);
        });

        const handlers = targets
            .filter(({ node }) => !node.closest('[data-rcs="root"]'))
            .map(({ node }) => {
                const enter = () => {
                    window.clearTimeout(hoverTimerRef.current);
                    setHoveredNode(node);
                };
                node.addEventListener('mouseenter', enter);
                return { node, enter };
            });
        const compareHandlers = Array.from(compareGroups.entries()).map(
            ([compareRoot, group]) => {
                const move = (event) => {
                    const { left, width } = compareRoot.getBoundingClientRect();
                    const handle = compareRoot.querySelector(
                        '[data-rcs="handle-container"]'
                    );
                    const position = Number(
                        handle?.getAttribute('aria-valuenow') || 50
                    );
                    const pointerPosition = width
                        ? ((event.clientX - left) / width) * 100
                        : 0;
                    const target =
                        pointerPosition >= position
                            ? group.after || group.before
                            : group.before || group.after;

                    if (target?.node) {
                        window.clearTimeout(hoverTimerRef.current);
                        setHoveredNode(target.node);
                    }
                };

                compareRoot.addEventListener('pointerenter', move);
                compareRoot.addEventListener('pointermove', move);
                return { compareRoot, move };
            }
        );
        root.addEventListener('mouseleave', clearHover);

        return () => {
            window.clearTimeout(hoverTimerRef.current);
            root.removeEventListener('mouseleave', clearHover);
            handlers.forEach(({ node, enter }) => {
                node.removeEventListener('mouseenter', enter);
            });
            compareHandlers.forEach(({ compareRoot, move }) => {
                compareRoot.removeEventListener('pointerenter', move);
                compareRoot.removeEventListener('pointermove', move);
            });
        };
    }, [rootRef, targets]);

    return targets.map(
        ({ node, key, value, isIcon, missingAltText, heavy }, targetIndex) => {
            const root = rootRef.current;
            if (!root || !node.isConnected) return null;

            const rootRect = root.getBoundingClientRect();
            const imageRect = node.getBoundingClientRect();
            const isHovered = hoveredNode === node;
            const setInputRef = (input) => {
                if (input) uploadInputRefs.current.set(key, input);
                else uploadInputRefs.current.delete(key);
            };

            return (
                <div
                    key={`${key}-${value}-${targetIndex}`}
                    className="pointer-events-none absolute z-[80]"
                    style={{
                        left: imageRect.left - rootRect.left,
                        top: imageRect.top - rootRect.top,
                        width: imageRect.width,
                        height: imageRect.height,
                    }}
                    onMouseEnter={() => {
                        window.clearTimeout(hoverTimerRef.current);
                        setHoveredNode(node);
                    }}
                >
                    <div
                        className={`absolute right-2 top-2 flex flex-col gap-1 rounded-lg bg-neutral-950/80 p-1 shadow-lg backdrop-blur-sm transition-opacity ${
                            isHovered
                                ? 'pointer-events-auto opacity-100'
                                : 'pointer-events-none opacity-0'
                        }`}
                        onMouseEnter={() => {
                            window.clearTimeout(hoverTimerRef.current);
                            setHoveredNode(node);
                        }}
                    >
                        <button
                            type="button"
                            title={isIcon ? 'Update icon' : 'Replace image'}
                            aria-label={
                                isIcon ? 'Update icon' : 'Replace image'
                            }
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors hover:bg-white/20"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onReplace({ key, value, isIcon });
                            }}
                            onPointerDown={() => {
                                window.clearTimeout(hoverTimerRef.current);
                                setHoveredNode(node);
                            }}
                        >
                            {isIcon ? (
                                <PencilIcon className="h-3.5 w-3.5" />
                            ) : (
                                <PhotoIcon className="h-4 w-4" />
                            )}
                        </button>
                        {!isIcon && (
                            <button
                                type="button"
                                title="Upload replacement image"
                                aria-label="Upload replacement image"
                                disabled={uploadingKey === key}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors hover:bg-white/20 disabled:cursor-wait disabled:opacity-50"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    uploadInputRefs.current.get(key)?.click();
                                }}
                                onPointerDown={() => {
                                    window.clearTimeout(hoverTimerRef.current);
                                    setHoveredNode(node);
                                }}
                            >
                                <ArrowUpTrayIcon className="h-4 w-4" />
                            </button>
                        )}
                        {!isIcon && missingAltText && (
                            <button
                                type="button"
                                title="Missing Alt Text"
                                aria-label="Missing Alt Text"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-yellow-300 transition-colors hover:bg-white/20"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onSeoWarning?.({ focus: 'alt-text' });
                                }}
                                onPointerDown={() => {
                                    window.clearTimeout(hoverTimerRef.current);
                                    setHoveredNode(node);
                                }}
                            >
                                <ExclamationTriangleIcon className="h-4 w-4" />
                            </button>
                        )}
                        {!isIcon && heavy && (
                            <button
                                type="button"
                                title="Image Too Heavy"
                                aria-label="Image Too Heavy"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-red-400 transition-colors hover:bg-white/20"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onSeoWarning?.({ focus: 'heavy' });
                                }}
                                onPointerDown={() => {
                                    window.clearTimeout(hoverTimerRef.current);
                                    setHoveredNode(node);
                                }}
                            >
                                <ExclamationCircleIcon className="h-4 w-4" />
                            </button>
                        )}
                        <input
                            ref={setInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (event) => {
                                const file = event.target.files?.[0];
                                event.target.value = '';
                                if (!file || typeof onUpload !== 'function')
                                    return;
                                setUploadingKey(key);
                                try {
                                    await onUpload({ key, file });
                                } finally {
                                    setUploadingKey(null);
                                }
                            }}
                        />
                    </div>
                </div>
            );
        }
    );
}
